/** IANA zone for a bridge's country (and state when the country spans multiple zones). */

const COUNTRY_TIMEZONES = {
  india: 'Asia/Kolkata',
  in: 'Asia/Kolkata',
  uk: 'Europe/London',
  gb: 'Europe/London',
  'united kingdom': 'Europe/London',
  usa: 'America/New_York',
  us: 'America/New_York',
  'united states': 'America/New_York',
  'united states of america': 'America/New_York',
  canada: 'America/Toronto',
  ca: 'America/Toronto',
  australia: 'Australia/Sydney',
  au: 'Australia/Sydney',
};

const STATE_TIMEZONES = {
  // USA
  alabama: 'America/Chicago',
  alaska: 'America/Anchorage',
  arizona: 'America/Phoenix',
  arkansas: 'America/Chicago',
  california: 'America/Los_Angeles',
  colorado: 'America/Denver',
  connecticut: 'America/New_York',
  delaware: 'America/New_York',
  florida: 'America/New_York',
  georgia: 'America/New_York',
  hawaii: 'Pacific/Honolulu',
  idaho: 'America/Boise',
  illinois: 'America/Chicago',
  indiana: 'America/Indiana/Indianapolis',
  iowa: 'America/Chicago',
  kansas: 'America/Chicago',
  kentucky: 'America/New_York',
  louisiana: 'America/Chicago',
  maine: 'America/New_York',
  maryland: 'America/New_York',
  massachusetts: 'America/New_York',
  michigan: 'America/Detroit',
  minnesota: 'America/Chicago',
  mississippi: 'America/Chicago',
  missouri: 'America/Chicago',
  montana: 'America/Denver',
  nebraska: 'America/Chicago',
  nevada: 'America/Los_Angeles',
  'new hampshire': 'America/New_York',
  'new jersey': 'America/New_York',
  'new mexico': 'America/Denver',
  'new york': 'America/New_York',
  'north carolina': 'America/New_York',
  'north dakota': 'America/Chicago',
  ohio: 'America/New_York',
  oklahoma: 'America/Chicago',
  oregon: 'America/Los_Angeles',
  pennsylvania: 'America/New_York',
  'rhode island': 'America/New_York',
  'south carolina': 'America/New_York',
  'south dakota': 'America/Chicago',
  tennessee: 'America/Chicago',
  texas: 'America/Chicago',
  utah: 'America/Denver',
  vermont: 'America/New_York',
  virginia: 'America/New_York',
  washington: 'America/Los_Angeles',
  'west virginia': 'America/New_York',
  wisconsin: 'America/Chicago',
  wyoming: 'America/Denver',
  'district of columbia': 'America/New_York',
  // Canada
  alberta: 'America/Edmonton',
  'british columbia': 'America/Vancouver',
  manitoba: 'America/Winnipeg',
  'new brunswick': 'America/Moncton',
  'newfoundland and labrador': 'America/St_Johns',
  'northwest territories': 'America/Yellowknife',
  'nova scotia': 'America/Halifax',
  nunavut: 'America/Iqaluit',
  ontario: 'America/Toronto',
  'prince edward island': 'America/Halifax',
  quebec: 'America/Toronto',
  saskatchewan: 'America/Regina',
  yukon: 'America/Whitehorse',
  // Australia
  'australian capital territory': 'Australia/Sydney',
  'new south wales': 'Australia/Sydney',
  'northern territory': 'Australia/Darwin',
  queensland: 'Australia/Brisbane',
  'south australia': 'Australia/Adelaide',
  tasmania: 'Australia/Hobart',
  victoria: 'Australia/Melbourne',
  'western australia': 'Australia/Perth',
};

const normalizeKey = (value) => String(value || '').trim().toLowerCase();

const hasExplicitOffset = (value) => /Z$|[+-]\d{2}:?\d{2}$/.test(value);

/**
 * API timestamps are UTC. Strings without a zone are treated as UTC so the
 * chart can convert them to the bridge location instead of the browser zone.
 */
export const toUtcDate = (timestamp) => {
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'number') return new Date(timestamp);
  if (typeof timestamp !== 'string') return new Date(timestamp);
  const value = timestamp.trim();
  if (!value) return new Date(NaN);
  if (hasExplicitOffset(value)) return new Date(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(`${value}Z`);
  }
  return new Date(value);
};

export const getBridgeTimeZone = (bridge) => {
  const stateZone = STATE_TIMEZONES[normalizeKey(bridge?.state)];
  if (stateZone) return stateZone;
  return COUNTRY_TIMEZONES[normalizeKey(bridge?.country)] || undefined;
};

const formatWithZone = (timestamp, timeZone, options) => {
  const date = toUtcDate(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const opts = { ...options };
  if (timeZone) opts.timeZone = timeZone;
  try {
    return date.toLocaleString('en-US', opts);
  } catch {
    return date.toLocaleString('en-US', options);
  }
};

/** Calendar day YYYY-MM-DD in the given IANA zone (same zone as chart axis labels). */
export const toDateKeyInTimeZone = (timestamp, timeZone) => {
  const date = toUtcDate(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const year = get('year');
    const month = get('month');
    const day = get('day');
    if (!year || !month || !day) return '';
    return `${year}-${month}-${day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

/** True when the timestamp's chart-axis calendar day is inside Start–End. */
export const isTimestampInChartDateRange = (timestamp, startDate, endDate, timeZone) => {
  if (!startDate || !endDate) return false;
  const dayKey = toDateKeyInTimeZone(timestamp, timeZone);
  if (!dayKey) return false;
  return dayKey >= startDate && dayKey <= endDate;
};

const zoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;
  const asUtc = Date.UTC(
    Number(get('year')),
    Number(get('month')) - 1,
    Number(get('day')),
    hour,
    Number(get('minute')),
    Number(get('second')),
  );
  return asUtc - date.getTime();
};

/** UTC instant for a wall-clock time on a calendar day in the bridge zone. */
export const zonedWallTimeToUtcMs = (dateKey, hour = 0, minute = 0, timeZone) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ''));
  if (!match) return NaN;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  if (!timeZone) return utcGuess;
  try {
    const utc = utcGuess - zoneOffsetMs(new Date(utcGuess), timeZone);
    return utcGuess - zoneOffsetMs(new Date(utc), timeZone);
  } catch {
    return utcGuess;
  }
};

/** Hour 0–23 in the bridge zone. */
export const zonedHour = (timestamp, timeZone) => {
  const date = toUtcDate(timestamp);
  if (Number.isNaN(date.getTime())) return 0;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    let hour = Number(parts.find((part) => part.type === 'hour')?.value);
    if (!Number.isFinite(hour) || hour === 24) return 0;
    return hour;
  } catch {
    return date.getUTCHours();
  }
};

/** Live-chart day tick, e.g. "Sep 28". */
export const formatChartDayLabel = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ''));
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

/** Live-chart hour tick, e.g. "13:00". */
export const formatChartHourLabel = (hour) => `${String(hour).padStart(2, '0')}:00`;

/** Live / temperature chart tick: MM/DD, HH:MM:SS AM/PM in bridge local time. */
export const formatChartAxisLabel = (timestamp, timeZone) =>
  formatWithZone(timestamp, timeZone, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

/** Chart tooltip timestamp in bridge local time. */
export const formatChartFullTime = (timestamp, timeZone) =>
  formatWithZone(timestamp, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

/** Same tooltip clock, with the stored millisecond, for a burst sample. */
export const formatChartSampleTime = (timestamp, timeZone) => {
  const base = formatChartFullTime(timestamp, timeZone);
  const date = toUtcDate(timestamp);
  if (!base || Number.isNaN(date.getTime())) return base;
  const ms = String(date.getUTCMilliseconds()).padStart(3, '0');
  if (/\b(?:AM|PM)\b/.test(base)) {
    return base.replace(/(\d{1,2}:\d{2}:\d{2})(?=\s*[AP]M)/, `$1.${ms}`);
  }
  return `${base}.${ms}`;
};
